import { ChevronSmallRightIcon as ChevronRight } from "@wags/ui/product-icons.tsx";

import { Link } from "@tanstack/react-router";
import { cn } from "@wags/ui/ui-utils";

type BaseMenuItem = {
    label: string;
    muted?: boolean;
};

export type MenuItem =
    | (BaseMenuItem & {
          to: string;
          href?: never;
          onClick?: never;
      })
    | (BaseMenuItem & {
          href: string;
          to?: never;
          onClick?: never;
      })
    | (BaseMenuItem & {
          onClick: () => void;
          to?: never;
          href?: never;
      });

export function MenuSection({
    label,
    items,
    className,
}: {
    label?: string;
    items: MenuItem[];
    className?: string;
}) {
    return (
        <section className={cn("flex flex-col font-family-body", className)}>
            {label && (
                <p className="pb-2 pl-4 text-[13px] font-medium leading-4 text-doberman-black/55 md:pl-5">
                    {label}
                </p>
            )}
            <div className="flex flex-col">
                {items.map((item) => (
                    <MenuRow key={item.label} item={item} />
                ))}
            </div>
        </section>
    );
}

function MenuRow({ item }: { item: MenuItem }) {
    const className = cn(
        "flex min-h-[53px] touch-manipulation items-center justify-between border-t border-doberman-black/8 px-4 py-4 text-left outline-none transition-colors duration-100 ease-[ease] active:bg-doberman-black/[0.03] motion-reduce:transition-none md:min-h-[59px] md:px-5",
        "[@media(hover:hover)]:hover:rounded-nested [@media(hover:hover)]:hover:border-transparent [@media(hover:hover)]:hover:bg-doberman-black/[0.02] [@media(hover:hover)]:[&:hover+*]:border-transparent",
        "active:rounded-nested active:border-transparent active:transition-none [&:active+*]:border-transparent [&:active+*]:transition-none",
        "focus-visible:rounded-nested focus-visible:border-transparent focus-visible:bg-doberman-black/[0.04] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-doberman-black/60 focus-visible:transition-none [&:focus-visible+*]:border-transparent [&:focus-visible+*]:transition-none",
        item.muted ? "text-doberman-black/60" : "text-doberman-black",
    );
    const contents = (
        <>
            <span className="text-[15px] font-medium leading-5 tracking-[-0.01em] md:text-[16px] md:leading-[22px]">
                {item.label}
            </span>
            <ChevronRight
                aria-hidden
                className="size-4 shrink-0 text-doberman-black/35"
            />
        </>
    );

    if (item.to) {
        return (
            <Link to={item.to} className={className}>
                {contents}
            </Link>
        );
    }

    if (item.href) {
        return (
            <a href={item.href} className={className}>
                {contents}
            </a>
        );
    }

    return (
        <button type="button" onClick={item.onClick} className={className}>
            {contents}
        </button>
    );
}
